import React from 'react';
import Script from 'next/script';
import PropTypes from 'prop-types';

import { generateSearchTag, mapFiltersAppliedEventData } from '@spa/shared/hooks/tracking/services/eulerian/mappers';
import { useAuth } from '@spa/shared/hooks/auth';

const getDefaultDataLayer = ({
  path,
  pagegroup,
  language,
  scart,
  scartcumul,
  reference,
  amount,
  status,
  type,
  payment,
  uid,
  newcustomer,
  email,
  profile,
  error,
  estimate,
  currency,
  prdref,
  prdname,
  prdgroup,
  prdamount,
  prdquantity,
  isearchengine
}) => ({
  path,
  pagegroup,
  language,
  ...(scart && { scart }),
  ...(scartcumul && { scartcumul }),
  ...(reference && { ref: reference }),
  ...(amount && { amount }),
  ...(status && { status }),
  ...(type && { type }),
  ...(payment && { payment }),
  ...(uid && { uid }),
  ...(newcustomer && { newcustomer }),
  ...(email && { email }),
  ...(profile && { profile }),
  ...(error && { error }),
  ...(estimate && { estimate }),
  ...(currency && { currency }),
  ...(prdref && { prdref }),
  ...(prdname && { prdname }),
  ...(prdgroup && { prdgroup }),
  ...(prdamount && { prdamount }),
  ...(prdquantity && { prdquantity }),
  ...(isearchengine && { isearchengine })
});

const getSignupDataLayer = ({ path, uid, email, profile, language }) => ({
  estimate: 1,
  pagegroup: 'signup',
  type: 'register',
  reference: `${uid}_${new Date().getTime()}`,
  path,
  language,
  uid,
  email,
  profile
});

const generateDataLayer = (dataObject, defaultTags = []) =>
  Object.entries(dataObject).reduce((acc, [key, value]) => [...acc, `${key}`, `${value}`], defaultTags);

export const SESSION_ID_KEY = 'eulerian:sessionId';

const getSessionId = newSessionId => {
  let sessionId = localStorage.getItem(SESSION_ID_KEY);

  if (!sessionId) {
    sessionId = newSessionId;
    localStorage.setItem(SESSION_ID_KEY, newSessionId);
  }

  return sessionId;
};

const Eulerian = ({
  checkoutParams,
  queryParams,
  pagegroup,
  sessionId,
  signedUp,
  defaultSearchTags = {},
  ...props
}) => {
  const { isLoggedIn } = useAuth();
  const defaultTags = Object.entries(defaultSearchTags).reduce(
    (acc, [key, value]) => [...acc, ...generateSearchTag(key, value)],
    []
  );
  const dataLayer = generateDataLayer(getDefaultDataLayer({ pagegroup, ...props }), defaultTags);

  if (typeof localStorage === 'undefined') {
    return;
  }

  return (
    <Script id="eulerian" strategy="lazyOnload">
      {`
            (function(e,a){var i=e.length,y=5381,k='script',s=window,v=document,o=v. createElement(k);for(;i;){i-=1;y=(y*33)^e.charCodeAt(i)}y='EA'+(y>>>=0);(function(e,a,s,y){s[a]=s[a]||function(){(s[y]=s[y]||[]).push(arguments);s[y].eah=e;};}(e,a,s,y));i=new Date/1E7|0;o.ea=y;y=i%26;o.async=1;o.src='//'+e+'/'+String.fromCharCode(97+y,122-y,65+y)+(i%1E3)+'.js?2';s=v.getElementsByTagName(k)[0];s.parentNode.insertBefore(o,s);})('${
              process.env.EULERIAN_URL
            }','EA_push');

            EA_push(${JSON.stringify(
              signedUp
                ? generateDataLayer(getSignupDataLayer(props), [])
                : [
                    ...dataLayer,
                    ...(!isLoggedIn && pagegroup === 'search'
                      ? generateSearchTag('sessionId', getSessionId(sessionId))
                      : []),
                    ...(queryParams && pagegroup === 'search' ? mapFiltersAppliedEventData(queryParams) : []),
                    ...(checkoutParams && pagegroup === 'checkout'
                      ? Object.entries(checkoutParams).reduce((acc, [key, value]) => [...acc, `${key}`, `${value}`], [])
                      : []),
                    ...(pagegroup === 'offer'
                      ? Object.entries({ pagegroup, ...props }).reduce(
                          (acc, [key, value]) => (dataLayer.includes(key) ? [...acc] : [...acc, `${key}`, `${value}`]),
                          []
                        )
                      : [])
                  ]
            )});

            window.EA_context = ${JSON.stringify([
              ...dataLayer,
              ...(checkoutParams && pagegroup === 'checkout'
                ? Object.entries(checkoutParams).reduce((acc, [key, value]) => [...acc, `${key}`, `${value}`], [])
                : [])
            ])};
        `}
    </Script>
  );
};

Eulerian.propTypes = {
  pagegroup: PropTypes.string.isRequired,
  queryParams: PropTypes.object,
  checkoutParams: PropTypes.object,
  sessionId: PropTypes.string,
  defaultSearchTags: PropTypes.object,
  signedUp: PropTypes.bool
};

export default Eulerian;
